import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useEffect, useRef } from "react"

// ─── Style tokens (single source of truth for all tables in MC) ───────────────

export const MC_TABLE_CONTAINER =
  "overflow-hidden rounded-xl border border-grey4"
export const MC_TABLE_TH =
  "whitespace-nowrap border-b border-grey4 bg-grey2 px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-[0.04em] text-grey5"
export const MC_TABLE_TD =
  "overflow-hidden border-b border-grey4/50 px-3 py-[9px] align-middle text-[11.5px] last-of-type:border-b-0"
export const MC_TABLE_TD_PRIMARY = cn(MC_TABLE_TD, "text-white")
export const MC_TABLE_TD_MUTED = cn(MC_TABLE_TD, "text-grey5")

// ─── Components ───────────────────────────────────────────────────────────────

/** Widths for resizable columns, keyed by the column id passed via `columnIds`. */
export type McTableColumnWidths = Record<string, number>

const DEFAULT_MIN_COLUMN_WIDTH = 60

interface McTableProps {
  headers: React.ReactNode[]
  children: React.ReactNode
  className?: string
  /**
   * Pins the header while the body scrolls. The container becomes the vertical
   * scroll region, so the parent must give it a bounded height (e.g. `h-full`
   * inside a flex column). The header cells are opaque (bg-grey2), so rows
   * scroll cleanly underneath.
   */
  stickyHeader?: boolean
  /**
   * Stable id per header, same order/length as `headers`. Required to enable
   * drag-to-resize — it's the key used in `columnWidths` and reported back
   * through `onColumnWidthsChange`. Columns without a current width stay
   * flexible and share the remaining space.
   */
  columnIds?: string[]
  columnWidths?: McTableColumnWidths
  onColumnWidthsChange?: (widths: McTableColumnWidths) => void
  /** Minimum width a column can be dragged down to. Defaults to 60px. */
  minColumnWidth?: number
  /** Per-column override of `minColumnWidth`, keyed by column id. */
  minColumnWidths?: McTableColumnWidths
  /**
   * The scroll container, handed out so a caller can virtualize its own tbody
   * against it. Only meaningful together with `stickyHeader`: that is what
   * makes this div the scroll region. Without it the div does not scroll, a
   * virtualizer measures no viewport, and the list renders empty — so passing
   * one without the other warns in development.
   */
  scrollRef?: React.Ref<HTMLDivElement>
  /**
   * Total rows the body stands for, header included — set it when the body is
   * virtualized, so a screen reader is told the real size instead of counting
   * the handful of <tr>s that happen to be mounted. Pair it with
   * `rowIndex` on each McTr.
   */
  ariaRowCount?: number
}

/** Renders a full <table> with a consistent header. Wrap rows in a <tbody>. */
export function McTable({
  headers,
  children,
  className,
  stickyHeader,
  columnIds,
  columnWidths,
  onColumnWidthsChange,
  minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
  minColumnWidths,
  scrollRef,
  ariaRowCount,
}: McTableProps) {
  useEffect(() => {
    if (import.meta.env.DEV && scrollRef && !stickyHeader) {
      // eslint-disable-next-line no-console -- development-only misuse warning
      console.warn(
        "McTable: `scrollRef` without `stickyHeader` hands out a container that does not scroll.",
      )
    }
  }, [scrollRef, stickyHeader])

  const resizable = !!(
    columnIds &&
    onColumnWidthsChange &&
    columnIds.length === headers.length
  )
  const resizedTotalWidth = resizable
    ? columnIds.reduce((sum, id) => sum + (columnWidths?.[id] ?? 0), 0)
    : 0

  return (
    <div
      ref={scrollRef}
      className={cn(
        MC_TABLE_CONTAINER,
        stickyHeader && "overflow-y-auto",
        resizable && "overflow-x-auto",
        className,
      )}
    >
      <table
        aria-rowcount={ariaRowCount}
        className={cn(!resizable && "min-w-full", resizable && "table-fixed")}
        style={
          resizable ? { width: `max(100%, ${resizedTotalWidth}px)` } : undefined
        }
      >
        {resizable && (
          <colgroup>
            {columnIds.map((id) => (
              <col
                key={id}
                style={
                  columnWidths?.[id] ? { width: columnWidths[id] } : undefined
                }
              />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {headers.map((header, i) => {
              const columnId = columnIds?.[i]
              const columnLabel =
                typeof header === "string" && header ? header : undefined
              return (
                <th
                  key={i}
                  // Named explicitly next to a resize handle: otherwise the
                  // handle's own label joins the header's accessible name.
                  aria-label={resizable && columnId ? columnLabel : undefined}
                  className={cn(
                    MC_TABLE_TH,
                    stickyHeader && "sticky top-0 z-10",
                    resizable && "relative overflow-hidden",
                  )}
                >
                  {header}
                  {resizable && columnId && (
                    <ColumnResizeHandle
                      columnId={columnId}
                      columnLabel={columnLabel}
                      columnWidths={columnWidths ?? {}}
                      onColumnWidthsChange={onColumnWidthsChange}
                      minColumnWidth={
                        minColumnWidths?.[columnId] ?? minColumnWidth
                      }
                    />
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        {children}
      </table>
    </div>
  )
}

interface ColumnResizeHandleProps {
  columnId: string
  /** The column's header text, which names the handle for screen readers. */
  columnLabel?: string
  columnWidths: McTableColumnWidths
  onColumnWidthsChange: (widths: McTableColumnWidths) => void
  /** Defaults to 60px, like McTable's `minColumnWidth`. */
  minColumnWidth?: number
}

const KEYBOARD_RESIZE_STEP = 10

/**
 * Drag handle pinned to a header cell's right edge; commits width live while
 * dragging, and in 10px steps on ←/→ once focused. The header cell must be
 * `position: relative` and carry its own `aria-label`, or this handle's label
 * joins the header's accessible name. Exported for tables that can't render
 * through McTable (DeclarationListV2's sv2- styled table).
 */
export function ColumnResizeHandle({
  columnId,
  columnLabel,
  columnWidths,
  onColumnWidthsChange,
  minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
}: ColumnResizeHandleProps) {
  const { translate } = useTranslation()
  const dragStateRef = useRef({ startX: 0, startWidth: 0 })

  const currentWidth = (thElement: HTMLElement | null) =>
    columnWidths[columnId] ?? thElement?.getBoundingClientRect().width ?? 0

  const commitWidth = (width: number) =>
    onColumnWidthsChange({
      ...columnWidths,
      [columnId]: Math.max(minColumnWidth, Math.round(width)),
    })

  const beginResize = (clientX: number, thElement: HTMLElement | null) => {
    dragStateRef.current = {
      startX: clientX,
      startWidth: currentWidth(thElement),
    }
  }

  const resizeTo = (clientX: number) => {
    const { startX, startWidth } = dragStateRef.current
    commitWidth(startWidth + (clientX - startX))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const direction =
      e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0
    if (!direction) return
    // The arrow keys would otherwise scroll the table sideways too.
    e.preventDefault()
    e.stopPropagation()
    commitWidth(
      currentWidth(e.currentTarget.closest("th")) +
        direction * KEYBOARD_RESIZE_STEP,
    )
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    beginResize(e.clientX, e.currentTarget.closest("th"))

    const handleMouseMove = (moveEvent: MouseEvent) =>
      resizeTo(moveEvent.clientX)
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const touch = e.touches[0]
    if (!touch) return
    beginResize(touch.clientX, e.currentTarget.closest("th"))

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const moveTouch = moveEvent.touches[0]
      if (!moveTouch) return
      // Dragging a 4px handle inside a scrollable table fights the browser's
      // own touch-scroll gesture — block it so the column follows the finger.
      moveEvent.preventDefault()
      resizeTo(moveTouch.clientX)
    }
    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
      document.removeEventListener("touchcancel", handleTouchEnd)
    }
    document.addEventListener("touchmove", handleTouchMove, { passive: false })
    document.addEventListener("touchend", handleTouchEnd)
    document.addEventListener("touchcancel", handleTouchEnd)
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={
        columnLabel
          ? translate("MC_TABLE_RESIZE_COLUMN_NAMED", { column: columnLabel })
          : translate("MC_TABLE_RESIZE_COLUMN")
      }
      aria-valuenow={columnWidths[columnId]}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className="absolute top-0 right-0 z-10 h-full w-1 cursor-col-resize touch-none bg-transparent select-none hover:bg-yellow1 focus-visible:bg-yellow1 focus-visible:outline-none"
    />
  )
}

/** Consistent table row with bottom border, last-row suppression, and hover. */
export function McTr({
  children,
  className,
  onClick,
  rowIndex,
}: {
  children: React.ReactNode
  className?: string
  onClick?: () => void
  /** 1-based position in the FULL table (header is row 1). Only needed when
   *  the body is virtualized — see McTable's `ariaRowCount`. */
  rowIndex?: number
}) {
  return (
    <tr
      aria-rowindex={rowIndex}
      onClick={onClick}
      className={cn(
        "border-b border-grey4/50 transition-colors last:border-0",
        onClick ? "cursor-pointer hover:bg-white/4" : "cursor-default",
        className,
      )}
    >
      {children}
    </tr>
  )
}

type McTdVariant = "default" | "primary" | "muted" | "accent"

interface McTdProps {
  variant?: McTdVariant
  mono?: boolean
  className?: string
  colSpan?: number
  children: React.ReactNode
}

/** Consistent table cell. Use variant for color: primary=white, muted=grey5, accent=yellow1. */
export function McTd({
  variant = "default",
  mono = false,
  className,
  colSpan,
  children,
}: McTdProps) {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        MC_TABLE_TD,
        variant === "primary" && "text-white",
        variant === "muted" && "text-grey5",
        variant === "accent" && "text-yellow1",
        mono && "font-mono",
        className,
      )}
    >
      {children}
    </td>
  )
}
